// src/pages/message.js
import React, {useState} from 'react';
import {
    Container,
    Drawer,
    Box,
    List,
    ListItem,
    ListItemText,
    Card,
    CardContent,
    CircularProgress,
    Tabs,
    Tab,
    Paper,
    Alert,
    Button,
    Typography
} from '@mui/material';
import {useEffect} from 'react';
import {getAffiliate} from '../api/affiliateApi';

// Kolm alamkomponenti, mille teed eraldi failides:
import SendMessage from '../components/SendMessage';
import SentMessage from '../components/SentMessage';
import GroupsMessage from '../components/GroupsMessage';
import Templates from '../components/Templates';

const menuItems = [
    {id: 'send-message', label: 'Send Message (+)', component: SendMessage},
    {id: 'sent-messages', label: 'Sent Messages', component: SentMessage},
    {id: 'groups', label: 'Groups', component: GroupsMessage},
    {id: 'templates', label: 'Templates', component: Templates},
];

export default function MessagePage() {
    const [activeComponent, setActiveComponent] = useState('sent-messages');
    const [affiliate, setAffiliate] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchAffiliate = async () => {
            try {
                setError(null);
                const data = await getAffiliate();

                // Validate that we have the expected data structure
                if (data && data.affiliate && data.affiliate.id) {
                    setAffiliate(data);
                } else {
                    setError('Invalid affiliate data received');
                }
            } catch (error) {
                console.error('Error fetching affiliate:', error);
                setError('Failed to load affiliate information');
            } finally {
                setIsLoading(false);
            }
        };
        fetchAffiliate();
    }, []);

    const handleRetry = () => {
        setIsLoading(true);
        setError(null);
        // Re-run the useEffect logic
        const fetchAffiliate = async () => {
            try {
                const data = await getAffiliate();
                if (data && data.affiliate && data.affiliate.id) {
                    setAffiliate(data);
                } else {
                    setError('Invalid affiliate data received');
                }
            } catch (error) {
                console.error('Error fetching affiliate:', error);
                setError('Failed to load affiliate information');
            } finally {
                setIsLoading(false);
            }
        };
        fetchAffiliate();
    };

    // Leiame array-st õige komponendi klassi
    const ActiveComponent =
        menuItems.find((item) => item.id === activeComponent)?.component || SentMessage;

    const handleMenuClick = (componentId) => {
        setActiveComponent(componentId);
    };

    // Error state component
    const ErrorState = () => (
        <Box sx={{ textAlign: 'center', py: 4 }}>
            <Alert severity="warning" sx={{ mb: 2 }}>
                <Typography variant="h6" gutterBottom>
                    Affiliate Not Found
                </Typography>
                <Typography variant="body2" sx={{ mb: 2 }}>
                    Please add an affiliate first before accessing messaging features.
                </Typography>
                <Button
                    variant="contained"
                    onClick={handleRetry}
                    sx={{ mr: 1 }}
                >
                    Retry
                </Button>
                <Button
                    variant="outlined"
                    onClick={() => window.location.href = '/my-affiliate'} // Adjust path as needed
                >
                    Add Affiliate
                </Button>
            </Alert>
        </Box>
    );

    return (
        <Container
            maxWidth={false}
            sx={{
                px: {xs: 0, sm: 0, md: 4},
                display: 'flex',
                flexDirection: 'column',
                backgroundColor: 'background.default',
            }}
        >
            {/* Horisontaalne mobiilimenüü */}
            <Paper
                sx={{
                    position: 'static',
                    top: 45,
                    left: 0,
                    right: 0,
                    zIndex: 1300,
                    display: {xs: 'block', md: 'none'},
                    overflow: 'hidden',
                }}
                elevation={1}
            >
                <Box
                    sx={{
                        overflowX: 'auto',
                        display: 'flex',
                        width: '100%',
                        WebkitOverflowScrolling: 'touch',
                        msOverflowStyle: 'none',
                        scrollbarWidth: 'none',
                        '&::-webkit-scrollbar': { display: 'none' },
                        position: 'relative',
                        '&::after': {
                            content: '""',
                            position: 'absolute',
                            right: 0,
                            top: 0,
                            height: '100%',
                            width: '20px',
                            background: 'linear-gradient(to right, transparent, rgba(255,255,255,0.9))',
                            pointerEvents: 'none',
                        },
                    }}
                >
                    <Tabs
                        value={activeComponent}
                        onChange={(event, newValue) => setActiveComponent(newValue)}
                        variant="scrollable"
                        scrollButtons={false}
                        TabIndicatorProps={{ style: { display: 'none' } }}
                        sx={{
                            minHeight: '36px',
                            width: '100%',
                            '& .MuiTabs-flexContainer': {
                                width: '100%',
                                display: 'flex',
                                '& > *': {
                                    flexGrow: 1,
                                },
                            },
                            '& .MuiTab-root': {
                                minHeight: '36px',
                                py: 0.75,
                                textTransform: 'none',
                            },
                        }}
                    >
                        {menuItems.map((item) => (
                            <Tab
                                key={item.id}
                                label={item.label}
                                value={item.id}
                                sx={{
                                    whiteSpace: 'nowrap',
                                    minWidth: 'auto',
                                    fontWeight: activeComponent === item.id ? 'bold' : 'normal',
                                    fontSize: '0.85rem',
                                    px: 1,
                                }}
                            />
                        ))}
                    </Tabs>
                </Box>
            </Paper>

            <Box sx={{display: "flex", flexGrow: 1}}>
                {/* Desktopi küljeriba */}
                <Drawer
                    variant="permanent"
                    sx={{
                        display: {xs: 'none', md: 'block'},
                        width: 240,
                        flexShrink: 0,
                        position: 'relative !important',
                    }}
                    PaperProps={{
                        sx: {
                            position: 'relative',
                        },
                    }}
                >
                    <Box sx={{width: 220, p: 2}}>
                        <List>
                            {menuItems.map((item) => (
                                <ListItem
                                    button
                                    key={item.id}
                                    onClick={() => handleMenuClick(item.id)}
                                    selected={activeComponent === item.id}
                                >
                                    <ListItemText primary={item.label}/>
                                </ListItem>
                            ))}
                        </List>
                    </Box>
                </Drawer>

                {/* Põhisisu */}
                <Box
                    sx={{
                        flexGrow: 1,
                        p: {xs: 0, sm: 0, md: 3},
                        backgroundColor: 'background.paper',
                        mt: { xs: 1, md: 0 },
                    }}
                >
                    <Card sx={{backgroundColor: 'background.paper', p: 0, pt: 2, pb: 2}}>
                        <CardContent>
                            {isLoading ? (
                                <Box sx={{display: 'flex', justifyContent: 'center', my: 5}}>
                                    <CircularProgress/>
                                </Box>
                            ) : error || !affiliate?.affiliate?.id ? (
                                <ErrorState />
                            ) : (
                                activeComponent === 'templates' ? (
                                    <ActiveComponent
                                        affiliate={affiliate.affiliate.id}
                                    />
                                ) : (
                                    <ActiveComponent
                                        affiliate={affiliate.affiliate.id}
                                        affiliateEmail={affiliate.affiliate.email}
                                    />
                                )
                            )}
                        </CardContent>
                    </Card>
                </Box>
            </Box>
        </Container>
    );
}